import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusDot, Amount, StatCard } from '@redpanda/ui';
import { formatTableDate, useRefetchOnFocus } from '@redpanda/utils';
import { useDashboardStore } from '../stores/pmDashboardStore';
import { projectTone, priorityTone, toneOf } from '../utils/statusTone';
import { isPastDue } from '../utils/dateUtils';

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { stats, error, fetchDashboardStats } = useDashboardStore();

  // 先顯示上次的數字（persist），再背景更新
  useEffect(() => {
    fetchDashboardStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnFocus(() => fetchDashboardStats());

  if (error && !stats) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  const myTasks = stats?.tasks.myTasks ?? [];
  const recentProjects = stats?.recentProjects ?? [];

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('dashboard.title', { ns: 'pages' })}</h1>
      </div>

      {/* 每天要看的 4 個數字（D8、§3-4）；數字不上色，逾期大於 0 才用紅字 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title={t('dashboard.stats.inProgressProjects', { ns: 'pages' })} value={stats?.projects.inProgress ?? 0} />
        <StatCard
          title={t('dashboard.stats.overdueTasks', { ns: 'pages' })}
          value={<span className={(stats?.tasks.overdue ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : ''}>{stats?.tasks.overdue ?? 0}</span>}
        />
        <Link to="/receivables" className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <StatCard title={t('dashboard.billable', { ns: 'pages' })} value={<Amount value={stats?.finance.billable?.amount ?? 0} />} />
        </Link>
        <Link to="/receivables" className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          <StatCard
            title={t('dashboard.pendingPayment', { ns: 'pages' })}
            value={<Amount value={stats?.finance.pendingAmount ?? 0} />}
            hint={(stats?.finance.paymentOverdue?.amount ?? 0) > 0
              ? <span className="text-red-600 dark:text-red-400">{t('dashboard.paymentOverdueHint', { ns: 'pages', amount: `$${stats!.finance.paymentOverdue!.amount.toLocaleString()}` })}</span>
              : undefined}
          />
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 我的待辦任務 */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base font-semibold text-fg">{t('dashboard.myTasks', { ns: 'pages' })}</h2>
            <Link to="/tasks" className="text-sm text-accent hover:underline">{t('dashboard.viewAll', { ns: 'pages' })}</Link>
          </div>
          <div className="bg-surface rounded-lg border border-line overflow-hidden">
            {myTasks.length === 0 ? (
              <p className="px-5 py-6 text-sm text-fg-secondary">{t('dashboard.noTasks', { ns: 'pages' })}</p>
            ) : (
              <ul className="divide-y divide-line">
                {myTasks.map((task: any) => {
                  const overdue = isPastDue(task.dueDate);
                  return (
                    <li key={task.id} className="px-5 py-3 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-fg truncate">{task.title}</p>
                        <Link to={`/projects/${task.project.id}?tab=tasks`} className="text-[13px] text-fg-secondary hover:text-accent transition-colors">
                          {task.project.name}
                        </Link>
                      </div>
                      <StatusDot tone={toneOf(priorityTone, task.priority)} label={t(`pmTasks.priority.${task.priority}`, { ns: 'pages' })} />
                      <span className={`w-24 text-right text-sm tabular-nums ${overdue ? 'text-red-600 dark:text-red-400' : 'text-fg-secondary'}`}>
                        {task.dueDate ? formatTableDate(task.dueDate, i18n.language) : '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* 最近專案 */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-base font-semibold text-fg">{t('dashboard.recentProjects', { ns: 'pages' })}</h2>
            <Link to="/projects" className="text-sm text-accent hover:underline">{t('dashboard.viewAll', { ns: 'pages' })}</Link>
          </div>
          <div className="bg-surface rounded-lg border border-line overflow-hidden">
            {recentProjects.length === 0 ? (
              <p className="px-5 py-6 text-sm text-fg-secondary">{t('dashboard.noProjects', { ns: 'pages' })}</p>
            ) : (
              <ul className="divide-y divide-line">
                {recentProjects.map((project: any) => (
                  <li key={project.id} className="px-5 py-3 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <Link to={`/projects/${project.id}`} className="block text-sm font-medium text-fg truncate hover:text-accent">
                        {project.name}
                      </Link>
                      <p className="text-[13px] text-fg-secondary tabular-nums">{formatTableDate(project.createdAt, i18n.language)}</p>
                    </div>
                    <StatusDot tone={toneOf(projectTone, project.status)} label={t(`pmProjects.status.${project.status}`, { ns: 'pages' })} />
                    <span className="w-28 text-right text-sm text-fg tabular-nums">
                      {project.totalBudget ? <Amount value={project.totalBudget} /> : <span className="text-fg-muted">—</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
